const Hero = ({ data }) => {
  if (!data) return null;

  return (
    <section className="py-20 px-6" data-testid="hero-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div data-testid="hero-content">
            {/* ACI Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-full mb-4">
              <div className="w-5 h-5 rounded bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <span className="text-[7px] font-black text-white">ACI</span>
              </div>
              <a
                href="https://adaptivecompoundintelligence.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-purple-800 tracking-wide hover:text-purple-900 transition-colors"
              >
                An Adaptive Compound Intelligence Platform
              </a>
            </div>

            {/* Patent Callout */}
            <div className="flex items-center gap-3 mb-6 px-4 py-3 bg-gradient-to-r from-gray-50 to-indigo-50/50 border border-gray-200 rounded-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">U.S. Patent Pending #63/987,765</p>
                <p className="text-xs text-gray-500">The only patented AI governance framework purpose-built for federal acquisition</p>
              </div>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold mb-6" style={{ lineHeight: '1.3' }}>
              <span className="hero-wave-line">{data.headlines[0]}</span>
              <span className="hero-wave-line">{data.headlines[1]}</span>
              <span className="hero-wave-line">{data.headlines[2]}</span>
            </h1>

            {/* Killer differentiator line */}
            <p className="text-lg font-semibold text-purple-700 mb-4">
              {data.differentiator}
            </p>
            
            <p className="text-lg text-gray-600 mb-8 max-w-2xl leading-relaxed">
              {data.subtitle}
            </p>

            {/* Human-Verified badge */}
            <div className="flex items-center gap-2 mb-6 text-sm text-gray-500 font-medium">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Every AI decision human-verified. Every action backed by a provable audit trail.
            </div>
            
            <div className="flex flex-wrap gap-4 mb-6">
              <a
                href={data.primaryCTA.link}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/50 transition-all hover:-translate-y-1"
                data-testid="hero-primary-cta"
              >
                {data.primaryCTA.text}
              </a>
              <a
                href={data.secondaryCTA.link}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-indigo-50 hover:border-purple-600 transition-all"
                data-testid="hero-secondary-cta"
              >
                {data.secondaryCTA.text}
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href="https://www.encoresvcsllc.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-purple-50 border border-purple-200 text-purple-700 text-xs font-bold px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors"
              >
                ★ Built by Encore Services · SDVOSB Prime
              </a>
              <a
                href="/financial-integration"
                className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-medium px-3 py-1.5 rounded-full hover:bg-indigo-100 transition-colors"
              >
                Federal Financial Systems
              </a>
              <span className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full">
                FedRAMP Architecture
              </span>
              <span className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full">
                SAM.gov Integrated
              </span>
            </div>
          </div>

          {/* Right Card */}
          <div className="relative" data-testid="hero-card">
            <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/10 rounded-3xl p-1 shadow-2xl shadow-purple-500/30">
              <div className="bg-white rounded-3xl p-8 border border-gray-200">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">{data.card.title}</h3>
                  <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold rounded-full">
                    {data.card.tag}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {data.card.summary}
                </p>
                <ul className="space-y-4 text-sm text-gray-600">
                  {data.card.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: feature }} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
